import moment from 'moment';
import { FC, ReactNode, useContext, useEffect, useRef, useState } from 'react';
import {
	useListNotifications,
	useReadNotifications,
	useSearchNotifications,
} from '../../../apis/hooks/notification';
import Button, { IButtonProps } from '../../../components/bootstrap/Button';
import OffCanvas, {
	OffCanvasBody,
	OffCanvasHeader,
	OffCanvasTitle,
} from '../../../components/bootstrap/OffCanvas';
import Popovers from '../../../components/bootstrap/Popovers';
import Icon from '../../../components/icon/Icon';
import ThemeContext from '../../../contexts/themeContext';
import useDarkMode from '../../../hooks/useDarkMode';
import { HeaderRight } from '../../../layout/Header/Header';
import { socket } from '../../../socket';
import { requestNotificationPermission, showNotification } from '../../../utils/notifications';
import CommonHeaderChat from './CommonHeaderChat';
import { toast } from 'react-toastify';

interface ICommonHeaderRightProps {
	beforeChildren?: ReactNode;
	afterChildren?: ReactNode;
}
const CommonHeaderRight: FC<ICommonHeaderRightProps> = ({ beforeChildren, afterChildren }) => {
	const [page, setPage] = useState(1);
	const [isConnected, setIsConnected] = useState(socket.connected);
	const [searchTerm, setSearchTerm] = useState('');
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();
	const { themeStatus } = useDarkMode();
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const [notification, setNotification] = useState<any[]>([]);
	const loader = useRef<HTMLDivElement | null>(null);
	const [offcanvasStatus, setOffcanvasStatus] = useState(false);

	const styledBtn: IButtonProps = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};

	const { data: notificationList, isLoading } = useListNotifications({
		perPage: 20,
		pageNo: page,
		receverId: localStorage.getItem('receverId'),
	});

	const { mutate: searchNotifications } = useSearchNotifications();
	const { mutate: readNotification } = useReadNotifications();

	/* ----------- LOAD LIST + MERGE ----------- */
	useEffect(() => {
		if (notificationList?.records?.length > 0) {
			setNotification((prev: any[]) => {
				const existingIds = new Set(prev.map((n) => n._id));
				const newRecords = notificationList.records.filter(
					(n: any) => !existingIds.has(n._id),
				);
				return page === 1 ? notificationList.records : [...prev, ...newRecords];
			});
		}
	}, [notificationList, page]);

	/* ----------- UNREAD COUNT ----------- */
	const unreadCount = notification.filter((n) => !n.isRead).length;

	/* ----------- INFINITE SCROLL ----------- */
	useEffect(() => {
		if (!loader.current) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting && !isLoading) {
					setPage((prev) => prev + 1);
				}
			},
			{ threshold: 1 },
		);

		observer.observe(loader.current);

		return () => observer.disconnect();
	}, [isLoading]);

	/* ----------- SOCKET CONNECT + EVENTS ----------- */
	useEffect(() => {
		socket.connect();
		requestNotificationPermission();

		const loginId = localStorage.getItem('loggedin_user_id');

		const onConnect = () => {
			if (loginId) setIsConnected(true);
		};

		const onDisconnect = () => setIsConnected(false);

		const onMessage = (value: any) => {
			const newNotification = {
				...value,
				isRead: false,
				createdAt: value?.createdAt || new Date(),
			};
			setNotification((prev: any[]) => [newNotification, ...prev]);

			// Toast by type
			switch (newNotification?.type) {
				case 'success':
					toast.success(newNotification?.message || 'Success notification');
					break;
				case 'warning':
					toast.warn(newNotification?.message || 'Warning notification');
					break;
				case 'error':
					toast.error(newNotification?.message || 'Error notification');
					break;
				default:
					toast.info(newNotification?.message || 'New notification');
			}

			showNotification("Neel's Purchase", {
				body: newNotification?.message || 'New notification',
			});
		};

		socket.on('connect', onConnect);
		socket.on('disconnect', onDisconnect);
		socket.on('notification', onMessage);

		return () => {
			socket.off('connect', onConnect);
			socket.off('disconnect', onDisconnect);
			socket.off('notification', onMessage);
		};
	}, []);

	/* ----------- SEARCH ----------- */
	const onChange = () => {
		if (searchTerm.length > 0) {
			searchNotifications(
				{ searchString: searchTerm, perPage: 10, pageNo: 1 },
				{
					onSuccess: (data: any) => {
						setNotification(data);
					},
				},
			);
		}
	};

	/* ----------- MARK AS READ ----------- */
	const onReadNotification = (id: string) => {
		const loginId = localStorage.getItem('loggedin_user_id');

		readNotification(
			{ _id: id, userId: loginId },
			{
				onSuccess: () => {
					setNotification((prev: any[]) =>
						prev.map((item) => (item._id === id ? { ...item, isRead: true } : item)),
					);
				},
			},
		);
	};

	const handleScroll = (e: React.UIEvent<HTMLUListElement>) => {
		if (notificationList?.records?.length > 0) {
			const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
			if (scrollHeight - scrollTop <= clientHeight + 10 && !isLoading) {
				setPage((prev) => prev + 1);
			}
		}
	};

	return (
		<HeaderRight>
			<div className='row g-3'>
				{/* {beforeChildren} */}
				{/* Tour Modal */}

				{/* Dark Mode */}
				<div className='col-auto'>
					<Popovers trigger='hover' desc='Dark / Light mode'>
						<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...styledBtn}
							onClick={() => setDarkModeStatus(!darkModeStatus)}
							className='btn-only-icon'
							data-tour='dark-mode'
							aria-label='Toggle dark mode'>
							<Icon
								icon={darkModeStatus ? 'DarkMode' : 'LightMode'}
								color={darkModeStatus ? 'info' : 'warning'}
								className='btn-icon'
							/>
						</Button>
					</Popovers>
				</div>

				{/*	Full Screen */}
				<div className='col-auto'>
					<Popovers trigger='hover' desc='Fullscreen'>
						<Button
							// eslint-disable-next-line react/jsx-props-no-spreading
							{...styledBtn}
							icon={fullScreenStatus ? 'FullscreenExit' : 'Fullscreen'}
							onClick={() => setFullScreenStatus(!fullScreenStatus)}
							aria-label='Toggle fullscreen'
						/>
					</Popovers>
				</div>

				{/*	Notifications */}
				<div className='col-auto relative' style={{ position: 'relative' }}>
					{unreadCount > 0 && (
						<span
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								backgroundColor: 'red',
								color: 'white',
								fontSize: '10px',
								minWidth: '20px',
								minHeight: '20px',
								position: 'absolute',
								borderRadius: '50%',
								zIndex: 99,
								right: 8,
							}}>
							{unreadCount}
						</span>
					)}
					<Button
						// eslint-disable-next-line react/jsx-props-no-spreading
						{...styledBtn}
						icon='Notifications'
						onClick={() => setOffcanvasStatus(true)}
						aria-label='Notifications'
					/>
				</div>
				{afterChildren}
				<CommonHeaderChat />
			</div>
			<OffCanvas
				id='notificationCanvas'
				titleId='offcanvasExampleLabel'
				placement='end'
				isOpen={offcanvasStatus}
				setOpen={setOffcanvasStatus}>
				<OffCanvasHeader setOpen={setOffcanvasStatus}>
					<OffCanvasTitle id='offcanvasExampleLabel'>Notifications</OffCanvasTitle>
					<div>
						{unreadCount > 0 && (
							<span
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									backgroundColor: 'red',
									color: 'white',
									fontSize: '15px',
									minWidth: '25px',
									minHeight: '20px',
									borderRadius: '50%',
									marginLeft: '0.5rem',
								}}>
								{unreadCount}
							</span>
						)}
					</div>
					<div style={{ marginLeft: '1rem' }}>
						<Button
							icon='Print'
							color='light'
							size={'sm'}
							tag='a'
							to='/notifications'
						// isLight
						>
							View All
						</Button>
					</div>
				</OffCanvasHeader>
				<OffCanvasBody>
					{/* <div className='d-flex mb-3'>
						<Input
							style={{ width: '96%' }}
							placeholder='search'
							id='search'
							name='search notifications'
							onChange={(e: any) => {
								setSearchTerm(e.target.value);
							}}
							value={searchTerm}
						/>
						<Button
							icon='search'
							color='primary'
							className='ms-2'
							isLight
							onClick={() => {												onChange();											setPerPage(10);											setCurrentPage(1);											}}></Button>
					</div> */}
					<ul
						className='list-group list-group-flush'
						onScroll={handleScroll}
						style={{ height: '90vh', overflow: 'auto' }}>
						{notification?.length === 0 && !isLoading && (
							<div>
								<div className='d-flex flex-column mb-2 p-3 cursor-pointer transition rounded'>
									No Notification Found
								</div>
							</div>
						)}
						{notification?.length > 0 &&
							notification?.map((item: any, index: number) => (
								<li
									onClick={() => onReadNotification(item?._id)}
									key={index}
									style={
										!item?.isRead
											? {
												backgroundColor: '#c4beed',
												color: 'black',
											}
											: {}
									}
									className='list-group-item d-flex flex-column mb-2 border-0 border-bottom p-3 cursor-pointer transition rounded'>
									<div className='d-flex justify-content-between'>
										{' '}
										<span className='fw-medium'>{item?.actionBy}</span>
										<span
											className={`${themeStatus === 'dark' ? 'text-black' : 'text-body'} `}>
											{moment(item?.createdAt?.replace(/Z$/, '')).format(
												'DD-MM-YYYY hh:mm A',
											)}
										</span>
									</div>
									<span className=''>{item?.message}</span>
								</li>
							))}
						<div ref={loader} style={{ height: '40px' }}>
							{isLoading && 'Loading...'}
						</div>
					</ul>
				</OffCanvasBody>
			</OffCanvas>
		</HeaderRight>
	);
};

export default CommonHeaderRight;
